import { isFederated } from '@frontend-platform/federation-runtime';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useIsDynamicEnv } from '@/shared/hooks/use-is-dynamic-env';
import { useSharedKey } from '@/shared/hooks/use-shared-key';
import { type BotEnvironment, getBotEnvironments } from '../api/environments-api';
import {
  ENVIRONMENT_OPTIONS,
  EP_ENV_DESCRIPTION,
  LIVE_RELEASE_ENV,
} from '../components/create-run/constants';
import { QK } from '../lib/query-keys';

// aistudio already fetches this ladder itself (Init/index.vue's
// loadEnvironmentData, re-run on every bot switch since BotSwitcher redirects
// back through Init) and publishes it on @frontend-platform/host-state — reading it here
// avoids a second GET /bot/environments?manifestDetails=true for data the
// host already has.
function sameEntries(a: BotEnvironment[], b: BotEnvironment[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((entry, i) => entry === b[i]);
}

// Last array handed out, so repeat publishes of an unchanged ladder keep
// returning one reference. Module-scoped rather than a ref: the store is a
// singleton, so every component observing it must agree on the snapshot.
let stableEnvironments: BotEnvironment[] | undefined;

/**
 * The host rebuilds this key as a fresh `.map(toRaw)` array on *every*
 * shared-state publish (aistudio `client/app.js`), and publishes on all seven
 * allowlisted mutations — so the reference changes even when the ladder
 * didn't. Handing that straight to `useSyncExternalStore` re-renders on
 * unrelated mutations like `SET_PASSPORT_USER`, and cascades: `data` identity
 * flows into `environmentOptions`, which re-derives `nonProdEnvironmentOptions`
 * and the `getEnvOption`/`resolveBotRefId` callbacks below.
 *
 * Entry-by-entry reference comparison is exact here rather than a heuristic,
 * because both host mutations that touch the ladder replace the entries they
 * change instead of mutating them: `SET_ENVIRONMENTS` assigns a wholly new
 * fetched array, and `UPDATE_MANIFEST_LABEL` maps to `{ ...env, manifestLabel }`
 * for the matched entry while passing the rest through untouched.
 *
 * Deliberately does not overwrite the cache when the contents match — the
 * returned reference has to stay identical across the repeat `getSnapshot()`
 * calls React makes per render, not just once per publish.
 */
function selectEnvironments(value: unknown): BotEnvironment[] | undefined {
  const next = value as BotEnvironment[] | undefined;
  if (next === stableEnvironments) return stableEnvironments;
  if (next && stableEnvironments && sameEntries(next, stableEnvironments)) {
    return stableEnvironments;
  }
  stableEnvironments = next;
  return next;
}

function useSharedEnvironments(): BotEnvironment[] | undefined {
  return useSharedKey('environments', selectEnvironments);
}

/** One entry of the Calibration environment axis, EP or non-EP. */
export interface CalibrationEnvironmentOption {
  label: string;
  value: string;
  description: string;
  /** EP only — the environment-scoped bot the conversation APIs must be asked about. */
  botRefId?: string;
  /** EP only — manifest of the env's active build, sent in the create-run payload. */
  manifestId?: string;
  envOrder?: number;
  /** EP only — raw build identifier of the env's active build, e.g. `build_v6 draft`. */
  manifestBuild?: string;
  /** EP only — active build label with its status suffix, e.g. `build_v6 (active)`. */
  buildLabel?: string;
  isLive: boolean;
}

const NON_EP_OPTIONS: CalibrationEnvironmentOption[] = ENVIRONMENT_OPTIONS.map((opt) => ({
  label: opt.label,
  value: opt.value,
  description: opt.description,
  isLive: opt.value === LIVE_RELEASE_ENV,
}));

/**
 * Single source of truth for Calibration's environment axis. Abstracts the two
 * worlds so no step component has to key off the literal SANDBOX/LIVE strings:
 *
 * - **EP bots** (`envProductType === 'DYNAMIC_ENV'`): environments come from the
 *   bot's promotion ladder — an ordered pipeline (`envOrder`) where each env
 *   carries its own active build (`manifestBuild` / `manifestId`) and its own
 *   `botRefId`. The env's form value is `releaseEnv`, so it can be any slug
 *   the bot defines (QA, UAT, PRE_PROD…), not just SANDBOX/LIVE. Read from
 *   `@frontend-platform/host-state` (aistudio already fetches this ladder itself); falls
 *   back to `GET /bot/environments?manifestDetails=true` directly only in
 *   standalone dev, where there's no host to have published it.
 * - **Non-EP bots**: the legacy hardcoded Staging (SANDBOX) / Production (LIVE)
 *   pair, with no build provenance.
 *
 * Ported from aistudio's `useCalibrationEnvironments` composable.
 */
export function useCalibrationEnvironments() {
  const isEP = useIsDynamicEnv();
  const sharedEnvironments = useSharedEnvironments();

  // Only fetch it ourselves in standalone dev (no host at all). Gating on
  // `isFederated()` rather than `sharedEnvironments === undefined` matters:
  // aistudio's own bootstrap is async, so @frontend-platform/host-state can still be a tick
  // behind `mount()` starting — checking the value alone can't tell "no host,
  // ever" apart from "host present, hasn't published yet" and would fire a
  // real request in the second case. Under federation we just wait; the
  // reactive read below picks up the publish on the next render.
  const { data: fetchedEnvironments } = useQuery({
    queryKey: QK.calibration.environments,
    queryFn: ({ signal }) => getBotEnvironments(signal),
    enabled: isEP && !isFederated(),
    staleTime: 5 * 60 * 1000,
  });

  const data = sharedEnvironments ?? fetchedEnvironments;

  const environmentOptions = useMemo<CalibrationEnvironmentOption[]>(() => {
    if (!isEP) {
      // Non-EP bots have no promotion ladder, so there's nothing to fetch —
      // but each environment still maps to its own environment-scoped bot,
      // and conversation-count/create-run calls need that botRefId to ask
      // about the right one. aistudio resolves it via socialConfig at the
      // federated module's own mount (FederatedView.vue) — regardless of
      // entry route, unlike the legacy Vue app's Overview-page-only fetch —
      // and publishes it through the same `environments` shared-state key
      // EP bots use, just with releaseEnv SANDBOX/LIVE instead of a real
      // ladder slug. Match on releaseEnv so the botRefId lands on the right
      // hardcoded option; undefined until that publish arrives, same as any
      // other shared-state read racing this component's first render.
      return NON_EP_OPTIONS.map((opt) => ({
        ...opt,
        botRefId: data?.find((env) => env.releaseEnv === opt.value)?.botRefId,
      }));
    }
    return (
      [...(data ?? [])]
        .sort((a, b) => (a.envOrder ?? 0) - (b.envOrder ?? 0))
        // An env with no active build has nothing to evaluate, so it can't be picked.
        .filter((env) => env.manifestBuild && env.manifestId)
        .map((env) => {
          const isLive = env.releaseEnv === LIVE_RELEASE_ENV;
          return {
            label: env.label || env.releaseEnv,
            value: env.releaseEnv,
            botRefId: env.botRefId,
            manifestId: env.manifestId,
            envOrder: env.envOrder,
            manifestBuild: env.manifestBuild,
            buildLabel: `${env.manifestBuild} (active)`,
            isLive,
            description: isLive ? EP_ENV_DESCRIPTION.LIVE : EP_ENV_DESCRIPTION.NON_PROD,
          };
        })
    );
  }, [isEP, data]);

  // Data-source dropdown (Scope step) lists only non-production environments.
  const nonProdEnvironmentOptions = useMemo(
    () => environmentOptions.filter((opt) => !opt.isLive),
    [environmentOptions]
  );

  const getEnvOption = useCallback(
    (value: string | undefined | null): CalibrationEnvironmentOption | null =>
      environmentOptions.find((opt) => opt.value === value) ?? null,
    [environmentOptions]
  );

  const isLiveEnv = useCallback(
    (value: string | undefined | null) => value === LIVE_RELEASE_ENV,
    []
  );

  const getEnvLabel = useCallback(
    (value: string | undefined | null): string => getEnvOption(value)?.label || value || '-',
    [getEnvOption]
  );

  // EP → the lowest env in the pipeline; non-EP → Staging.
  const defaultEnvValue = environmentOptions[0]?.value ?? '';

  /**
   * The environment-scoped bot the conversation-count APIs must be asked about.
   * Both EP and non-EP bots have one `botRefId` per env; for non-EP it's
   * undefined until aistudio's socialConfig-derived publish lands in
   * `environments` (see the non-EP branch of `environmentOptions` above),
   * in which case this falls back to `null` — the host's own bot-ref-id
   * header applies until then, same as it always did.
   */
  const resolveBotRefId = useCallback(
    (value: string | undefined | null): string | null => getEnvOption(value)?.botRefId ?? null,
    [getEnvOption]
  );

  return {
    isEP,
    environmentOptions,
    nonProdEnvironmentOptions,
    getEnvOption,
    isLiveEnv,
    getEnvLabel,
    defaultEnvValue,
    resolveBotRefId,
  };
}
